import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getDirectory, getOrganigrama, getMyTimeOff, requestTimeOff, cancelTimeOff, getTimeOffInbox, decideTimeOff,
  getHolidays, getSaldoVacaciones,
} from '../api/client'
import Calendario from '../components/personas/Calendario'
import { useAuth } from '../context/AuthContext'
import PageHeader, { Ico } from '../components/PageHeader'
import Modal from '../components/Modal'
import { detalleError } from '../components/admin/detalle'
import { toISODate } from '../utils/weeks'
import { PEDIBLES, TIPO_COLORES, ESTADO_COLORES, rangoFechas, diasCorridos } from '../utils/ausencias'
import { arbolAreas, aplanarArbol } from '../utils/arbolAreas'

// Personas: the company directory, for everyone signed in, in any workspace
// (routers/directorio.py) -- who is who, how to reach them, who is away --
// and leave requests (routers/time_off.py): ask for your own ("Mis
// vacaciones"), and, as an area head or admin, decide your people's ("Por
// aprobar"). Notifications link here with ?mias=1 and ?solicitudes=1.
// "Organigrama" (?organigrama=1) draws the areas' tree with their heads;
// tapping a unit opens the directory filtered to it.

const PROXIMOS_DIAS = 14     // the "who is away" strip looks this far ahead
const MAX_EN_TARJETA = 3     // upcoming leave listed on a person's card

// Accent- and case-insensitive, as the backend's norm_name.
const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

function iniciales(nombre) {
  return (nombre || '?').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

const PESTANAS = { directorio: {}, organigrama: { organigrama: '1' }, calendario: { calendario: '1' },
  mias: { mias: '1' }, aprobar: { solicitudes: '1' } }

// Weekdays from one ISO date to another, holidays out (`feriados`: a Set of
// ISO dates) -- the form's estimate; the server's dias_habiles is the count.
function diasHabiles(desde, hasta, feriados) {
  let n = 0
  const d = new Date(desde + 'T00:00:00'), fin = new Date(hasta + 'T00:00:00')
  while (d <= fin) {
    const iso = toISODate(d)
    if (![0, 6].includes(d.getDay()) && !feriados.has(iso)) n++
    d.setDate(d.getDate() + 1)
  }
  return n
}

// Days left in Buk, under a request or at the top of Mis vacaciones.
function Saldo({ t, saldo, compacto }) {
  if (!saldo?.disponible) return null
  const s = saldo.saldo || {}
  const partes = ['legales', 'dias_administrativos'].filter(k => s[k] != null)
    .map(k => t(`saldo.${k}`, { count: s[k] }))
  if (!partes.length) return null
  return compacto
    ? <span className="text-xs text-gray-500">{t('saldo.titulo')}: {partes.join(' · ')}</span>
    : (
      <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-1">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{t('saldo.titulo')}</span>
        {partes.map(p => <span key={p} className="text-sm text-gray-800">{p}</span>)}
        <span className="text-xs text-gray-400">{t('saldo.fuente')}</span>
      </div>
    )
}

export default function Personas() {
  const { t } = useTranslation(['personas', 'common'])
  const { user } = useAuth()
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const isAdmin = roles.includes('admin')
  const tienePersona = user?.team_member_id != null

  // The tab lives in the URL, so a notification's link opens the right one
  // even when the page is already open.
  const [params, setParams] = useSearchParams()
  const tab = params.get('solicitudes') ? 'aprobar' : params.get('mias') ? 'mias'
    : params.get('organigrama') ? 'organigrama' : params.get('calendario') ? 'calendario' : 'directorio'
  const irA = (p) => setParams(PESTANAS[p], { replace: true })

  const [personas, setPersonas] = useState(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [area, setArea] = useState('')
  const [organigrama, setOrganigrama] = useState(null)   // [{ id, code, name, parent_id, jefes, personas }]
  const [vistaOrg, setVistaOrg] = useState('jefatura')     // who reports to whom, or the areas' tree
  const [errorOrg, setErrorOrg] = useState('')

  const [mias, setMias] = useState(null)
  const [inbox, setInbox] = useState([])
  const [pidiendo, setPidiendo] = useState(false)
  const [feriados, setFeriados] = useState([])
  const [saldo, setSaldo] = useState(null)        // mine, from Buk

  const cargarDirectorio = () => getDirectory({ dias: 60 })
    .then(setPersonas).catch(() => setError(t('loadFailed')))
  const cargarOrganigrama = () => getOrganigrama()
    .then(setOrganigrama).catch(() => setErrorOrg(t('organigrama.loadFailed')))
  const cargarMias = () => getMyTimeOff().then(setMias).catch(() => setMias([]))
  const cargarInbox = () => getTimeOffInbox().then(setInbox).catch(() => setInbox([]))

  useEffect(() => {
    cargarDirectorio(); cargarOrganigrama(); cargarMias(); cargarInbox()
    getHolidays({}).then(setFeriados).catch(() => {})
    if (user?.team_member_id != null) getSaldoVacaciones(user.team_member_id).then(setSaldo).catch(() => {})
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  // The sidebar's badge counts the inbox too; tell it to look again.
  const avisarInbox = () => window.dispatchEvent(new Event('time-off-inbox-changed'))

  // Area options: the org chart's units as a tree (each gerencia, its áreas
  // indented below), plus any area someone belongs to that it lacks (should
  // it fail to load). `nivel` is the depth, for the indent.
  const areas = useMemo(() => {
    const out = aplanarArbol(organigrama || []).map(({ area: a, nivel }) => ({ code: a.code, name: a.name, nivel }))
    const vistos = new Set(out.map(a => a.code))
    const extra = new Map()
    for (const p of personas || []) for (const a of p.areas) if (!vistos.has(a.code)) extra.set(a.code, a.name)
    return [...out, ...[...extra].map(([code, name]) => ({ code, name, nivel: 0 }))
      .sort((a, b) => a.name.localeCompare(b.name))]
  }, [personas, organigrama])

  // An area and every unit below it: picking a gerencia shows its áreas' people too.
  const subarbol = useMemo(() => {
    const { hijos } = arbolAreas(organigrama || [])
    const porCodigo = new Map((organigrama || []).map(a => [a.code, a]))
    const cache = new Map()
    return (code) => {
      if (!cache.has(code)) {
        const out = new Set([code])
        const pila = [porCodigo.get(code)].filter(Boolean)
        while (pila.length) {
          const a = pila.pop()
          for (const h of hijos.get(a.id) || []) if (!out.has(h.code)) { out.add(h.code); pila.push(h) }
        }
        cache.set(code, out)
      }
      return cache.get(code)
    }
  }, [organigrama])

  const deArea = useMemo(() => {
    if (!area) return personas || []
    const codigos = subarbol(area)
    return (personas || []).filter(p => p.areas.some(a => codigos.has(a.code)))
  }, [personas, area, subarbol])
  const visibles = useMemo(() => {
    const n = norm(q.trim())
    if (!n) return deArea
    return deArea.filter(p => norm(p.name).includes(n) || norm(p.email).includes(n) || norm(p.role).includes(n))
  }, [deArea, q])

  const verAprobar = isAdmin || inbox.length > 0 || tab === 'aprobar'
  const pendientesMias = (mias || []).filter(r => r.estado === 'pendiente').length

  const pestanas = [
    { id: 'directorio', label: t('tabs.directorio') },
    { id: 'organigrama', label: t('tabs.organigrama') },
    { id: 'calendario', label: t('tabs.calendario') },
    { id: 'mias', label: t('tabs.mias'), count: pendientesMias, tono: 'gray' },
    ...(verAprobar ? [{ id: 'aprobar', label: t('tabs.aprobar'), count: inbox.length, tono: 'red' }] : []),
  ]

  return (
    <div className="p-8">
      <div className="mb-5">
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          actions={tienePersona && (
            <button onClick={() => setPidiendo(true)}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 inline-flex items-center gap-1.5 transition-colors">
              <Ico.Plus /> {t('mias.pedir')}
            </button>
          )}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        {pestanas.map(p => (
          <button key={p.id} onClick={() => irA(p.id)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
              tab === p.id ? 'border-gray-900 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            {p.label}
            {p.count > 0 && (
              <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                p.tono === 'red' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'
              }`}>{p.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'directorio' && (
        <Directorio
          t={t} personas={personas} error={error} visibles={visibles}
          q={q} setQ={setQ} area={area} setArea={setArea} areas={areas} miId={user?.team_member_id}
          subarbol={subarbol} feriados={feriados} onVerCalendario={() => irA('calendario')}
        />
      )}

      {tab === 'organigrama' && (
        <>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 mb-4">
            {['jefatura', 'areas'].map(v => (
              <button key={v} type="button" onClick={() => setVistaOrg(v)}
                className={`px-3 py-1 rounded-md text-xs font-medium ${vistaOrg === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {t(`organigrama.vista.${v}`)}
              </button>
            ))}
          </div>
          {vistaOrg === 'jefatura'
            ? <Jefatura t={t} personas={personas} miId={user?.team_member_id} />
            : <Organigrama
                t={t} areas={organigrama} error={errorOrg}
                onVer={(code) => { setArea(code); setQ(''); irA('directorio') }}
              />}
        </>
      )}

      {tab === 'calendario' && (
        <Calendario t={t} personas={personas} areas={areas} subarbol={subarbol} miId={user?.team_member_id} feriados={feriados}
          puedeGestionarEventos={!isAdmin && roles.includes('rrhh')} />
      )}

      {tab === 'mias' && (
        <MisVacaciones
          t={t} mias={mias} saldo={saldo} tienePersona={tienePersona} onPedir={() => setPidiendo(true)}
          onCambio={() => { cargarMias(); cargarDirectorio() }}
        />
      )}

      {tab === 'aprobar' && (
        <PorAprobar
          t={t} inbox={inbox} personas={personas || []}
          onDecidida={(id) => {
            setInbox(rows => rows.filter(r => r.id !== id))
            avisarInbox(); cargarDirectorio()
          }}
        />
      )}

      {pidiendo && (
        <PedirModal
          t={t} saldo={saldo} feriados={feriados} onClose={() => setPidiendo(false)}
          onPedida={() => {
            setPidiendo(false)
            cargarMias(); cargarDirectorio(); cargarInbox(); avisarInbox()
            irA('mias')
          }}
        />
      )}
    </div>
  )
}

// ── Badges ───────────────────────────────────────────────────────────────────

function TipoBadge({ t, tipo }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORES[tipo] || 'bg-gray-100 text-gray-600'}`}>
      {t(`tipos.${tipo}`, tipo)}
    </span>
  )
}

function EstadoBadge({ t, estado }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORES[estado] || 'bg-gray-100 text-gray-600'}`}>
      {t(`estados.${estado}`, estado)}
    </span>
  )
}

// ── Directorio ───────────────────────────────────────────────────────────────

function Directorio({ t, personas, error, visibles, q, setQ, area, setArea, areas, miId, subarbol, feriados, onVerCalendario }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Ico.Search /></span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('buscar')}
            className="border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm w-80 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200" />
        </div>
        <select value={area} onChange={e => setArea(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200">
          <option value="">{t('todasAreas')}</option>
          {areas.map(a => <option key={a.code} value={a.code}>{'   '.repeat(a.nivel || 0)}{a.name}</option>)}
        </select>
        {personas && (
          <span className="text-xs text-gray-400">{t('cuenta', { count: visibles.length })}</span>
        )}
      </div>

      {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

      {personas === null ? (
        !error && <div className="text-gray-400 text-sm">{t('cargando')}</div>
      ) : (
        <>
          {/* Who is away, today and the next days: the calendar's grid, in this area. */}
          <Calendario t={t} personas={personas} areas={areas} subarbol={subarbol} miId={miId} feriados={feriados}
            ventana={PROXIMOS_DIAS} areaFija={area} onVerMas={onVerCalendario} />
          {visibles.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">{t('sinResultados')}</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {visibles.map(p => <Tarjeta key={p.id} t={t} p={p} soyYo={p.id === miId} />)}
            </div>
          )}
        </>
      )}
    </>
  )
}

function Tarjeta({ t, p, soyYo }) {
  const proximas = p.ausencias
  return (
    <div className={`bg-white rounded-xl border p-4 flex flex-col gap-3 ${p.ausente_hoy ? 'border-amber-200' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
          <span className="text-xs font-semibold text-white">{iniciales(p.name)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-gray-900 leading-tight truncate" title={p.name}>
              {p.name}
              {soyYo && <span className="ml-1.5 text-xs font-normal text-gray-400">({t('tu')})</span>}
            </p>
            {p.ausente_hoy && (
              <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
                {t('ausenteHoy')}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate" title={p.role || ''}>{p.role || t('sinCargo')}</p>
          {/* Only sent to Administración and RRHH. */}
          {p.rut && <p className="text-[11px] text-gray-400 mt-0.5 tabular-nums">RUT {p.rut}</p>}
          {p.supervisor && (
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">{t('reportaA', { nombre: p.supervisor })}</p>
          )}
        </div>
      </div>

      {p.areas.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {p.areas.map(a => (
            <span key={a.area_id} title={a.es_jefe ? t('jefe') : undefined}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs ${
                a.es_jefe ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
              {a.es_jefe && <span aria-label={t('jefe')}>★</span>}
              {a.name}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-1 text-sm">
        {p.email
          ? <a href={`mailto:${p.email}`} className="block text-gray-700 hover:text-gray-900 hover:underline truncate">{p.email}</a>
          : <p className="text-gray-300 text-xs">{t('sinEmail')}</p>}
        {p.phone && (
          <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`} className="block text-gray-700 hover:text-gray-900 hover:underline">{p.phone}</a>
        )}
      </div>

      {proximas.length > 0 && (
        <div className="border-t border-dashed border-gray-200 pt-2.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">{t('proximas')}</p>
          <ul className="space-y-1">
            {proximas.slice(0, MAX_EN_TARJETA).map(a => (
              <li key={a.id} className={`flex items-center gap-2 text-xs ${a.estado === 'pendiente' ? 'opacity-80' : ''}`}>
                <TipoBadge t={t} tipo={a.type} />
                <span className="text-gray-600">{rangoFechas(a.start_date, a.end_date)}{a.half_day ? ` · ${t('medioDia')}` : ''}</span>
                {a.estado === 'pendiente' && (
                  <span className="px-1.5 py-0.5 rounded border border-dashed border-amber-300 text-amber-700 text-[10px]">
                    {t('estados.pendiente')}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {proximas.length > MAX_EN_TARJETA && (
            <p className="text-[11px] text-gray-400 mt-1">{t('masAusencias', { count: proximas.length - MAX_EN_TARJETA })}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Organigrama ──────────────────────────────────────────────────────────────

// The areas' tree, a card per unit with its heads and people, its units
// below indented under a line. Collapsible; on a phone the indentation
// shrinks so deep trees still fit.
function Organigrama({ t, areas, error, onVer }) {
  const [cerradas, setCerradas] = useState(() => new Set())
  const { raices, hijos } = useMemo(() => arbolAreas(areas || []), [areas])

  if (error) return <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>
  if (areas === null) return <div className="text-gray-400 text-sm">{t('cargando')}</div>
  if (!areas.length) {
    return <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-xl text-sm max-w-4xl">{t('organigrama.vacio')}</div>
  }

  const conHijos = areas.filter(a => hijos.has(a.id))
  const alternar = (id) => setCerradas(c => {
    const n = new Set(c)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const Nodo = ({ a, vistos }) => {
    if (vistos.has(a.id)) return null         // a cycle; the server refuses them
    const sigue = new Set(vistos).add(a.id)
    const debajo = hijos.get(a.id) || []
    const abierta = !cerradas.has(a.id)
    return (
      <li>
        <div className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors flex items-stretch">
          <button type="button" onClick={() => onVer(a.code)} title={t('organigrama.verPersonas', { area: a.name })}
            className="flex-1 min-w-0 text-left px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold text-gray-900 truncate">{a.name}</span>
              <span className="shrink-0 text-xs text-gray-500 tabular-nums">{t('organigrama.personas', { count: a.personas })}</span>
            </div>
            {a.jefes.length ? (
              <ul className="mt-1.5 space-y-0.5">
                {a.jefes.map(j => (
                  <li key={j.id} className="flex items-baseline gap-1.5 text-sm min-w-0">
                    <span className="text-gray-900 shrink-0" aria-label={t('jefe')}>★</span>
                    <span className="text-gray-800 truncate">{j.name}</span>
                    {j.role && <span className="text-xs text-gray-500 truncate">· {j.role}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-gray-400">{t('organigrama.sinJefe')}</p>
            )}
          </button>
          {debajo.length > 0 && (
            <button type="button" onClick={() => alternar(a.id)} aria-expanded={abierta}
              title={t(abierta ? 'organigrama.contraer' : 'organigrama.expandir', { area: a.name })}
              className="shrink-0 px-3 border-l border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-r-xl flex flex-col items-center justify-center gap-0.5">
              <span className={`text-sm transition-transform ${abierta ? 'rotate-90' : ''}`} aria-hidden="true">›</span>
              <span className="text-[10px] whitespace-nowrap">{t('organigrama.subareas', { count: debajo.length })}</span>
            </button>
          )}
        </div>
        {debajo.length > 0 && abierta && (
          <ul className="mt-2 ml-2 sm:ml-5 pl-2 sm:pl-4 border-l-2 border-gray-200 space-y-2">
            {debajo.map(h => <Nodo key={h.id} a={h} vistos={sigue} />)}
          </ul>
        )}
      </li>
    )
  }

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="text-sm text-gray-500">{t('organigrama.ayuda')}</p>
        {conHijos.length > 0 && (
          <div className="flex gap-3 text-xs shrink-0">
            <button type="button" onClick={() => setCerradas(new Set())} className="text-gray-500 hover:text-gray-900">
              {t('organigrama.expandirTodo')}
            </button>
            <button type="button" onClick={() => setCerradas(new Set(conHijos.map(a => a.id)))} className="text-gray-500 hover:text-gray-900">
              {t('organigrama.contraerTodo')}
            </button>
          </div>
        )}
      </div>
      <ul className="space-y-2">
        {raices.map(a => <Nodo key={a.id} a={a} vistos={new Set()} />)}
      </ul>
    </div>
  )
}

// ── Jefatura: who reports to whom (supervisor_id, from HR's nómina) ─────────

function Jefatura({ t, personas, miId }) {
  const [cerrados, setCerrados] = useState(() => new Set())
  if (!personas) return <p className="text-sm text-gray-400">{t('cargando', { defaultValue: 'Cargando…' })}</p>
  const ids = new Set(personas.map(p => p.id))
  const hijos = new Map()
  for (const p of personas) {
    const jefe = p.supervisor_id != null && ids.has(p.supervisor_id) && p.supervisor_id !== p.id ? p.supervisor_id : null
    if (!hijos.has(jefe)) hijos.set(jefe, [])
    hijos.get(jefe).push(p)
  }
  const raices = hijos.get(null) || []
  const toggle = id => setCerrados(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const Nodo = ({ p, vistos }) => {
    if (vistos.has(p.id)) return null                 // a loop in the data: drawn once
    const debajo = (hijos.get(p.id) || []).slice().sort((a, b) => a.name.localeCompare(b.name))
    const abierto = !cerrados.has(p.id)
    const siguiente = new Set(vistos).add(p.id)
    return (
      <li>
        <div className={`flex items-center gap-2 rounded-lg border bg-white px-3 py-2 ${p.id === miId ? 'border-gray-900' : 'border-gray-200'}`}>
          {debajo.length > 0 ? (
            <button type="button" onClick={() => toggle(p.id)} className="text-gray-400 hover:text-gray-700 w-4 text-xs">
              {abierto ? '▾' : '▸'}
            </button>
          ) : <span className="w-4" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
            <p className="text-[11px] text-gray-500 truncate">{p.role || t('sinCargo')}</p>
          </div>
          {debajo.length > 0 && (
            <span className="text-[11px] text-gray-400 shrink-0">{t('organigrama.reportan', { count: debajo.length })}</span>
          )}
        </div>
        {abierto && debajo.length > 0 && (
          <ul className="ml-4 sm:ml-6 mt-2 space-y-2 border-l border-gray-200 pl-3">
            {debajo.map(h => <Nodo key={h.id} p={h} vistos={siguiente} />)}
          </ul>
        )}
      </li>
    )
  }

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-gray-500 mb-4">{t('organigrama.ayudaJefatura')}</p>
      <ul className="space-y-2">
        {raices.slice().sort((a, b) => (hijos.get(b.id)?.length || 0) - (hijos.get(a.id)?.length || 0) || a.name.localeCompare(b.name))
          .map(p => <Nodo key={p.id} p={p} vistos={new Set()} />)}
      </ul>
    </div>
  )
}

// ── Mis vacaciones ───────────────────────────────────────────────────────────

function MisVacaciones({ t, mias, saldo, tienePersona, onPedir, onCambio }) {
  const [error, setError] = useState('')
  const [retirando, setRetirando] = useState(null)

  const hoy = toISODate(new Date())
  // A pending request, or approved leave that hasn't started (removed from
  // Buk too). Leave made in Buk is changed in Buk.
  const retirable = (r) => r.origen !== 'buk'
    && (r.estado === 'pendiente' || (r.estado === 'aprobada' && r.start_date > hoy))
  const retirar = async (r) => {
    if (!window.confirm(t(r.estado === 'aprobada' ? 'mias.anularConfirm' : 'mias.retirarConfirm'))) return
    setError(''); setRetirando(r.id)
    try {
      await cancelTimeOff(r.id)
      onCambio()
    } catch (err) {
      setError(detalleError(err, t('mias.retirarFailed')))
    }
    setRetirando(null)
  }

  if (!tienePersona) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-5 py-4 text-sm max-w-2xl">
        {t('mias.sinPersona')}
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{t('mias.ayuda')}</p>
        <button onClick={onPedir}
          className="bg-white border border-gray-200 text-gray-800 px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-1.5 transition-colors shrink-0">
          <Ico.Plus /> {t('mias.pedir')}
        </button>
      </div>

      <Saldo t={t} saldo={saldo} />

      {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

      {mias === null ? (
        <div className="text-gray-400 text-sm">{t('cargando')}</div>
      ) : mias.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-xl text-sm">{t('mias.vacio')}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {mias.map(r => {
            const cerrada = r.estado === 'rechazada' || r.estado === 'cancelada'
            return (
              <div key={r.id} className={`px-5 py-3.5 flex items-start gap-4 ${cerrada ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-medium text-gray-900 ${r.estado === 'cancelada' ? 'line-through' : ''}`}>
                      {rangoFechas(r.start_date, r.end_date)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {r.half_day && r.start_date === r.end_date
                        ? (r.mitad ? t(`calendario.${r.mitad}`) : t('medioDia'))
                        : t('diasHabiles', { count: r.dias_habiles })}
                    </span>
                    <TipoBadge t={t} tipo={r.type} />
                    <EstadoBadge t={t} estado={r.estado} />
                    {r.origen === 'buk' && <span className="text-[10px] font-medium text-gray-400 border border-gray-200 rounded px-1">Buk</span>}
                  </div>
                  {r.buk_envio === 'error' && (
                    <p className="text-xs text-amber-700 mt-1">{t('mias.bukError')}</p>
                  )}
                  {r.notes && <p className="text-xs text-gray-500 mt-1">{r.notes}</p>}
                  {r.decidida_por && (r.estado === 'aprobada' || r.estado === 'rechazada') && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t(r.estado === 'aprobada' ? 'mias.aprobadaPor' : 'mias.rechazadaPor', { nombre: r.decidida_por })}
                      {r.decision_note && <span className="text-gray-700"> — “{r.decision_note}”</span>}
                    </p>
                  )}
                  {!r.decidida_por && r.decision_note && (
                    <p className="text-xs text-gray-700 mt-1">“{r.decision_note}”</p>
                  )}
                </div>
                {retirable(r) && (
                  <button onClick={() => retirar(r)} disabled={retirando === r.id}
                    className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-40 shrink-0 mt-0.5">
                    {t('mias.retirar')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Por aprobar ──────────────────────────────────────────────────────────────

function PorAprobar({ t, inbox, personas, onDecidida }) {
  if (inbox.length === 0) {
    return <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-xl text-sm max-w-4xl">{t('aprobar.vacio')}</div>
  }
  return (
    <div className="space-y-3 max-w-4xl">
      <p className="text-sm text-gray-500">{t('aprobar.ayuda')}</p>
      {inbox.map(r => <Solicitud key={r.id} t={t} r={r} personas={personas} onDecidida={onDecidida} />)}
    </div>
  )
}

// Who else of the requester's team -- same boss, or an area in common -- is
// away (approved or pending) on any of those days.
function tambienFuera(personas, r) {
  const yo = personas.find(p => p.id === r.team_member_id)
  if (!yo) return []
  const misAreas = new Set(yo.areas.map(a => a.code))
  return personas
    .filter(p => p.id !== yo.id && ((yo.supervisor_id && p.supervisor_id === yo.supervisor_id)
      || p.areas.some(a => misAreas.has(a.code))))
    .filter(p => p.ausencias.some(a => (a.estado === 'aprobada' || a.estado === 'pendiente')
      && a.start_date <= r.end_date && a.end_date >= r.start_date))
    .map(p => p.name)
}

function Solicitud({ t, r, personas, onDecidida }) {
  const [nota, setNota] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [saldo, setSaldo] = useState(null)
  useEffect(() => { getSaldoVacaciones(r.team_member_id).then(setSaldo).catch(() => {}) }, [r.team_member_id])
  const otros = useMemo(() => tambienFuera(personas, r), [personas, r])

  const decidir = async (aprobar) => {
    setEnviando(true); setError('')
    try {
      await decideTimeOff(r.id, aprobar, nota.trim())
      onDecidida(r.id)
    } catch (err) {
      setError(detalleError(err, t('aprobar.failed')))
      setEnviando(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-gray-900">{r.persona}</span>
        <TipoBadge t={t} tipo={r.type} />
        <span className="text-sm text-gray-700">{rangoFechas(r.start_date, r.end_date)}</span>
        <span className="text-xs text-gray-400">
          {r.half_day && r.mitad ? t(`calendario.${r.mitad}`) : r.half_day ? t('medioDia') : t('diasHabiles', { count: r.dias_habiles })}
        </span>
      </div>
      <div className="mt-1"><Saldo t={t} saldo={saldo} compacto /></div>
      {otros.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">{t('aprobar.tambienFuera', { nombres: otros.join(', ') })}</p>
      )}
      {r.notes && <p className="text-sm text-gray-600 mt-1.5">“{r.notes}”</p>}
      {r.solicitada_por && <p className="text-xs text-gray-400 mt-1">{t('aprobar.solicitadaPor', { nombre: r.solicitada_por })}</p>}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input value={nota} onChange={e => setNota(e.target.value)} placeholder={t('aprobar.notaPlaceholder')}
          className="flex-1 min-w-48 border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200" />
        <button onClick={() => decidir(true)} disabled={enviando}
          className="bg-emerald-600 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 transition-colors">
          {t('aprobar.aprobar')}
        </button>
        <button onClick={() => decidir(false)} disabled={enviando}
          className="bg-white border border-red-200 text-red-600 px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-40 transition-colors">
          {t('aprobar.rechazar')}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>
  )
}

// ── Pedir vacaciones ─────────────────────────────────────────────────────────

// jornada: 'full' | 'am' (morning off) | 'pm' (afternoon off) -- a single day only
const VACIA = { start_date: '', end_date: '', type: 'PTO', jornada: 'full', notes: '' }

function PedirModal({ t, saldo, feriados, onClose, onPedida }) {
  const [form, setForm] = useState(VACIA)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  // Half a day only makes sense for a single day.
  const unDia = !!form.start_date && form.start_date === form.end_date

  const fechas = (cambio) => setForm(f => {
    const g = { ...f, ...cambio }
    // Picking a start date with no end yet (or an end before it) fills the end.
    if (cambio.start_date && (!g.end_date || g.end_date < g.start_date)) g.end_date = g.start_date
    if (g.start_date !== g.end_date) g.jornada = 'full'
    return g
  })

  // Days asked vs. Buk's balance for this kind (the server also counts the
  // person's other requests not yet in Buk, and has the last word).
  const rango = form.start_date && form.end_date && form.end_date >= form.start_date
  const pedidos = !rango ? 0 : unDia && form.jornada !== 'full' ? 0.5
    : diasHabiles(form.start_date, form.end_date, new Set(feriados.map(f => f.date)))
  const clave = { PTO: 'legales', admin: 'dias_administrativos' }[form.type]
  const quedan = saldo?.disponible && clave ? saldo.saldo?.[clave] : undefined
  const excede = quedan != null && pedidos > quedan

  const enviar = async (e) => {
    e.preventDefault()
    if (!form.start_date || !form.end_date) { setError(t('pedir.fechasRequeridas')); return }
    if (form.end_date < form.start_date) { setError(t('pedir.finAntesInicio')); return }
    setEnviando(true); setError('')
    try {
      await requestTimeOff({
        start_date: form.start_date, end_date: form.end_date, type: form.type,
        half_day: unDia && form.jornada !== 'full', mitad: unDia && form.jornada !== 'full' ? form.jornada : null,
        notes: form.notes.trim() || null,
      })
      onPedida()
    } catch (err) {
      setError(detalleError(err, t('pedir.failed')))
      setEnviando(false)
    }
  }

  const campo = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'
  return (
    <Modal title={t('pedir.titulo')} onClose={onClose}>
      <form onSubmit={enviar} className="space-y-4">
        {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('pedir.tipo')}</label>
          <select className={campo} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            {PEDIBLES.map(tp => <option key={tp} value={tp}>{t(`tipos.${tp}`)}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pedir.inicio')}</label>
            <input type="date" className={campo} value={form.start_date}
              onChange={e => fechas({ start_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pedir.fin')}</label>
            <input type="date" className={campo} value={form.end_date} min={form.start_date || undefined}
              onChange={e => fechas({ end_date: e.target.value })} />
          </div>
        </div>
        {unDia && (
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {['full', 'am', 'pm'].map(j => (
              <button key={j} type="button" onClick={() => setForm(f => ({ ...f, jornada: j }))}
                className={`px-3 py-1 rounded-md text-xs font-medium ${form.jornada === j ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {t(`pedir.jornada.${j}`)}
              </button>
            ))}
          </div>
        )}
        {form.start_date && form.end_date && form.end_date >= form.start_date && (
          <p className="text-xs text-gray-500">
            {unDia && form.jornada !== 'full'
              ? t(`calendario.${form.jornada}`)
              : t('diasHabiles', { count: diasHabiles(form.start_date, form.end_date, new Set(feriados.map(f => f.date))) })}
            {' · '}{t('dias', { count: diasCorridos(form.start_date, form.end_date) })}
          </p>
        )}
        {form.type !== 'work_trip' && <Saldo t={t} saldo={saldo} compacto />}
        {excede && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            {t('pedir.excede', { pedidos, quedan })}
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('pedir.notas')}</label>
          <textarea className={`${campo} resize-none`} rows={2} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('pedir.notasPlaceholder')} />
        </div>
        <p className="text-xs text-gray-400">{t('pedir.aviso')}</p>
        <div className="flex gap-3 pt-1">
          <button type="submit" disabled={enviando || excede}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">
            {enviando ? t('pedir.enviando') : t('pedir.enviar')}
          </button>
          <button type="button" onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
            {t('pedir.cancelar')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
